import Button from "@/common/components/Button";
import Modal from "@/common/components/Modal";
import { useState } from "react";
import useBillingEventUploadFiscalDocumentForConsumption from "../../hooks/useBillingEventUploadFiscalDocumentForConsumption";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import { useQueryClient } from "@tanstack/react-query";
import { MultipleDocumentUploader } from "@/common/components/MultipleDocumentUploader";
import { ErrorOption, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { uploadFiscalDocumentsSchema, UploadFiscalDocumentsValidationSchema } from "../../schemas/uploadFiscalDocuments.schema";
import useBlobs from "@/common/hooks/queries/useBlobs";
import { ErrorMessage } from "@/common/components/ErrorMessage";

const UploadFiscalDocument = ({ eventId }: { eventId: string; }) => {
  const queryClient = useQueryClient();

  const [modalIsOpen, setModalIsOpen] = useState(false);

  const { 
    control,
    reset,
    handleSubmit,
    setError,
    formState: { errors }
   } = useForm<UploadFiscalDocumentsValidationSchema>({
    resolver: zodResolver(uploadFiscalDocumentsSchema)
  })

  const [messages, setMessages] = useState<string[] | boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);

  const { mutateAsync: uploadBlob, isPending: uploadBlobIsPending } = useBlobs()

  const { mutate: uploadFiscalDocumentForConsumption } = useBillingEventUploadFiscalDocumentForConsumption({
    id: eventId,
    onSuccess: () => {
      reset()
      setModalIsOpen(false);
      setIsError(false);
      setMessages(["Nota enviada com sucesso!"]);
    },
    onError: (error) => {
      if (error.response?.data.errors.base) {
        reset()
        setModalIsOpen(false);
        setIsError(true);
        setMessages(error.response?.data.errors.base);
      }
    }
  });

  const onSubmit = (data: UploadFiscalDocumentsValidationSchema) => {
    uploadFiscalDocumentForConsumption(data.fiscal_documents);
  };

  return (
    <>
      <Modal
        isOpen={modalIsOpen}
        setIsOpen={(value) => {
          setModalIsOpen(value);
          reset();
        }}
        Trigger={(
          <Button>Upload de nota</Button>
        )}
        title="Nova nota fiscal"
        footer="Confirmar upload manual"
        footerClassName="bg-primary-blue text-white-800"
        onConfirm={handleSubmit(onSubmit)}
        body={(
          <div className="flex flex-col max-h-[400px] overflow-y-auto thin-scroll px-1">
            <MultipleDocumentUploader
              className="flex-col w-full bg-yellow-op-2 text-white-400 border-none rounded-lg"
              containerClassName="flex-col-reverse"
              fileListClassName="flex-col"
              fileItemClassName="w-full text-white-400"
              title="Anexe a nota fiscal"
              control={control}
              name="fiscal_documents"
              fieldName="fiscal_documents"
              uploadBlob={uploadBlob}
              accept=".xml"
              disabled={uploadBlobIsPending}
              setError={(error) => error && setError('fiscal_documents', error as ErrorOption)}
            />
            {errors.fiscal_documents?.message && (
              <ErrorMessage
                name="file"
                message={errors.fiscal_documents?.message}
              />
            )}
          </div>
        )}
      />

      {messages && (
        <ModalMessageSimple
          messages={messages as string[]}
          setMessages={setMessages}
          isError={isError}
          onClose={() => {
            queryClient.invalidateQueries({ queryKey: ['billingEvent', eventId] });
            queryClient.invalidateQueries({ queryKey: ['billingEvents'] });
          }}
        />
      )}
    </>
  );
};

export default UploadFiscalDocument;
